import type { ParentType } from '@risksmart-app/domain/src/types/consts/parent-type';
import { useMemo } from 'react';
import type { Location } from 'react-router';
import { useLocation, useMatches } from 'react-router';

import type {
  BreadcrumbItem,
  BreadcrumbNodeLabelItemConfig,
  Match,
} from './types';
import { useNodeLabelStore } from './useNodeLabelStore';

/**
 * Parse route matches to extract nodes that need label fetching
 * @param matches - Array of route matches
 * @returns Array of breadcrumb nodes that need label fetching
 */
const extractNodes = (matches: Match[]): BreadcrumbNodeLabelItemConfig[] => {
  return matches.reduce<BreadcrumbNodeLabelItemConfig[]>((acc, match) => {
    const handle = match.handle;

    if (handle?.breadcrumbNode) {
      const nodeId = match.params[handle.breadcrumbNode.paramName];

      if (nodeId) {
        return [
          ...acc,
          {
            nodeType: handle.breadcrumbNode.nodeType,
            nodeId,
          },
        ];
      }
    }

    return acc;
  }, []);
};

/**
 * Build breadcrumb items from matches, using cached labels where available
 * @param matches - Array of route matches
 * @param location - Current location object
 * @param getLabel - Function to get cached label for a node
 * @returns Array of breadcrumb items with resolved labels
 */
const buildBreadcrumbs = (
  matches: Match[],
  location: Location,
  getLabel: (nodeType: ParentType, nodeId: string) => string | null | undefined
): BreadcrumbItem[] => {
  return matches.reduce<BreadcrumbItem[]>((acc, match) => {
    const { handle } = match;

    if (!handle || (!handle?.title && !handle?.breadcrumbNode)) {
      return acc;
    }

    const href = handle?.breadcrumbUrl
      ? handle.breadcrumbUrl({ match, location })
      : match.pathname;

    const handleBreadcrumbNodeId = handle?.breadcrumbNode
      ? match.params[handle.breadcrumbNode.paramName]
      : null;

    const handleBreadcrumbNodeText =
      handle.breadcrumbNode && handleBreadcrumbNodeId
        ? getLabel(handle.breadcrumbNode.nodeType, handleBreadcrumbNodeId)
        : null;

    const handleTitleText = handle?.title
      ? typeof handle.title === 'function'
        ? handle.title({ match, location })
        : handle.title
      : null;

    const text = handleBreadcrumbNodeText || handleTitleText || null;

    if (text) {
      return [...acc, { text, href }];
    }

    return acc;
  }, []);
};

/**
 * Hook to get breadcrumbs, fetching dynamic labels as needed and caching them in a Zustand store
 *
 * This hook:
 * 1. Extracts node IDs from route params
 * 2. Returns nodes to be fetched (fetching happens in NodeLabelFetcher component)
 * 3. Builds breadcrumb array with resolved labels from Zustand store
 * 4. Memoizes everything to prevent infinite rerenders
 *
 * @returns Object containing breadcrumbs array, showBreadcrumbs flag, and nodes to fetch
 */
export function useBreadcrumbs() {
  // react-router's useMatches() returns UIMatch<unknown, unknown>[] — it has no compile-time
  // knowledge of what each route's handle contains. Our route configs attach BreadcrumbHandle
  // by convention, but that contract isn't visible to TypeScript here. This cast trusts the
  // convention; if a route ever attaches a non-BreadcrumbHandle handle, downstream code will
  // get garbage rather than a type error. (Match.params is also narrower than UIMatch's:
  // we type it as Record<string, string> but useMatches actually returns string | undefined.
  // Consumers below null-check before use, so this is safe in practice.)
  const matches = useMatches() as Match[];
  const location = useLocation();
  const { getLabel, pending } = useNodeLabelStore();

  const nodes = useMemo(() => {
    return extractNodes(matches);
  }, [matches]);

  const breadcrumbs = useMemo(() => {
    return buildBreadcrumbs(matches, location, getLabel);
    // Including additional 'pending' dep for eager fetching to prevent stale cache issues
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, location, pending, getLabel]);

  return {
    breadcrumbs,
    showBreadcrumbs: breadcrumbs.length >= 2,
    nodes,
  };
}
